import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Heading, Text, Button, VStack, HStack, SimpleGrid, Card, CardBody,
  Badge, Spinner, Flex, Icon, IconButton, Input, Select, useToast, Divider, Image,
  Table, Thead, Tbody, Tr, Th, Td, Tooltip, Checkbox,
} from '@chakra-ui/react';
import {
  FaMapMarkerAlt, FaRoute, FaCar, FaClock, FaPlay, FaRedo, FaSyringe,
  FaArrowUp, FaArrowDown, FaExternalLinkAlt, FaUser, FaGripVertical, FaTimes,
  FaUserPlus, FaCalculator, FaChevronDown, FaChevronUp,
} from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { supabase } from '../../supabaseClient';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../utils/googleMapsLoader';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const mapContainerStyle = { width: '100%', height: '600px', borderRadius: '16px' };
const defaultCenter = { lat: 40.6892, lng: -74.0445 }; // NYC area

// Custom map styling for cleaner look
const mapStyles = [
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'simplified' }] },
];

const statusColor = (s) => {
  if (s === 'completed') return 'green';
  if (s === 'in_progress') return 'blue';
  if (s?.includes('waiting')) return 'orange';
  return 'purple';
};

export default function RoutePlanner({ phlebotomistId: phlebIdProp, hideHeader, adminMode } = {}) {
  const params = useParams();
  const phlebotomistId = phlebIdProp || params.id;
  const navigate = useNavigate();
  const toast = useToast();
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const [phleb, setPhleb] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stops, setStops] = useState([]);
  const [directions, setDirections] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [geocoding, setGeocoding] = useState(false);
  const [hasGeocoded, setHasGeocoded] = useState(false);
  const [selectedStopIds, setSelectedStopIds] = useState(new Set());
  const [showDirectionsPanel, setShowDirectionsPanel] = useState(false);
  const [departureTime, setDepartureTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [newPatientAddress, setNewPatientAddress] = useState('');
  const [newPatientEta, setNewPatientEta] = useState(null); // { direct: {eta, durationText, distanceText}, after: {...}, formattedAddress }
  const [newPatientLoading, setNewPatientLoading] = useState(false);
  const [showNewPatientPanel, setShowNewPatientPanel] = useState(false);
  const [phlebLatLng, setPhlebLatLng] = useState(null); // {lat, lng} of phleb company address

  // Format Date to "10:30 AM"
  const formatClock = (d) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  // Cumulative ETA at end of leg index `legIndex` (i.e. arrival at stop[legIndex] when phleb origin is used)
  const getEtaAtLeg = (legIndex) => {
    if (!routeInfo?.legs || !departureTime) return null;
    const [hh, mm] = departureTime.split(':').map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    const t = new Date();
    t.setHours(hh, mm, 0, 0);
    let totalSec = 0;
    for (let j = 0; j <= legIndex; j++) {
      if (routeInfo.legs[j]?.durationValue) totalSec += routeInfo.legs[j].durationValue;
    }
    t.setSeconds(t.getSeconds() + totalSec);
    return t;
  };

  // Build a Date from departureTime + offset seconds
  const etaFromOffset = (offsetSec) => {
    if (!departureTime) return null;
    const [hh, mm] = departureTime.split(':').map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    const t = new Date();
    t.setHours(hh, mm, 0, 0);
    t.setSeconds(t.getSeconds() + (offsetSec || 0));
    return t;
  };

  // Estimate ETA for a brand-new patient address (e.g., during a phone call to schedule)
  const estimateNewPatientEta = async () => {
    if (!newPatientAddress.trim()) {
      toast({ title: 'Enter a patient address first', status: 'warning', duration: 2500 });
      return;
    }
    if (!isLoaded || !window.google) return;
    setNewPatientLoading(true);
    setNewPatientEta(null);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const dest = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: newPatientAddress }, (results, status) => {
          if (status === 'OK' && results[0]) resolve(results[0]);
          else reject(new Error(status));
        });
      });
      const destLatLng = { lat: dest.geometry.location.lat(), lng: dest.geometry.location.lng() };
      const formattedAddress = dest.formatted_address;

      const directionsService = new window.google.maps.DirectionsService();

      // 1. Direct ETA — from phleb origin (or first stop) straight to new address
      const phlebOrigin = phleb?.company_address || (stops.find(s => s.lat && s.lng)?.address) || null;
      let direct = null;
      let directDirections = null;
      if (phlebOrigin) {
        try {
          const r = await new Promise((resolve, reject) => {
            directionsService.route({
              origin: phlebOrigin,
              destination: destLatLng,
              travelMode: window.google.maps.TravelMode.DRIVING,
            }, (res, st) => st === 'OK' ? resolve(res) : reject(new Error(st)));
          });
          const leg = r.routes[0].legs[0];
          direct = {
            durationText: leg.duration.text,
            durationValue: leg.duration.value,
            distanceText: leg.distance.text,
            eta: etaFromOffset(leg.duration.value),
          };
          directDirections = r;
        } catch (e) { /* skip if fails */ }
      }

      // 2. After-route ETA — append to end of currently selected stops
      let afterRoute = null;
      const selected = stops.filter(s => s.lat && s.lng && selectedStopIds.has(s.id));
      if (selected.length > 0) {
        // Use existing routeInfo total drive seconds if route is already optimized; otherwise compute
        let baseSec = null;
        if (routeInfo?.legs?.length) {
          baseSec = routeInfo.legs.reduce((acc, l) => acc + (l.durationValue || 0), 0);
        }
        // Compute leg from last selected stop to new patient
        const lastStop = selected[selected.length - 1];
        try {
          const r = await new Promise((resolve, reject) => {
            directionsService.route({
              origin: { lat: lastStop.lat, lng: lastStop.lng },
              destination: destLatLng,
              travelMode: window.google.maps.TravelMode.DRIVING,
            }, (res, st) => st === 'OK' ? resolve(res) : reject(new Error(st)));
          });
          const leg = r.routes[0].legs[0];
          if (baseSec === null) {
            // No optimized route yet — only show added leg duration
            afterRoute = {
              addedDurationText: leg.duration.text,
              addedDistanceText: leg.distance.text,
              eta: null,
              note: 'Optimize the route to see total arrival time',
            };
          } else {
            const totalSec = baseSec + leg.duration.value;
            afterRoute = {
              addedDurationText: leg.duration.text,
              addedDistanceText: leg.distance.text,
              totalDriveMin: Math.round(totalSec / 60),
              eta: etaFromOffset(totalSec),
            };
          }
        } catch (e) { /* skip */ }
      }

      // Auto-render the direct route on the map so user can see it visually.
      // Stash any existing optimized route first so we can restore it when the user clicks Clear.
      let stashedRoute = null;
      if (directDirections) {
        if (directions || routeInfo) {
          stashedRoute = { directions, routeInfo };
        }
        setDirections(directDirections);
        if (mapRef.current && window.google) {
          const bounds = new window.google.maps.LatLngBounds();
          directDirections.routes[0]?.legs?.[0]?.steps?.forEach((step) => {
            if (step.start_location) bounds.extend(step.start_location);
            if (step.end_location) bounds.extend(step.end_location);
          });
          if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, 80);
        }
      }

      setNewPatientEta({ formattedAddress, direct, afterRoute, destLatLng, directDirections, stashedRoute });
    } catch (err) {
      toast({ title: 'Could not find that address', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setNewPatientLoading(false);
    }
  };

  // Fetch phlebotomist profile
  useEffect(() => {
    setPhlebLatLng(null);
    const fetchPhleb = async () => {
      const { data } = await supabase.from('phlebotomist_profiles').select('full_name, company_name, email, phone, company_address').eq('id', phlebotomistId).single();
      if (data) setPhleb(data);
    };
    fetchPhleb();
  }, [phlebotomistId]);

  // Geocode the phleb's company address so the map can center on their office by default
  useEffect(() => {
    if (!isLoaded || !window.google || !phleb || phlebLatLng) return;
    if (!phleb.company_address) {
      console.warn('Phleb has no company_address — map will fall back to stops/default');
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: phleb.company_address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setPhlebLatLng({ lat: loc.lat(), lng: loc.lng() });
      } else {
        console.warn('Could not geocode phleb company_address:', phleb.company_address, status);
      }
    });
  }, [isLoaded, phleb, phlebLatLng]); // eslint-disable-line

  // Whenever the phleb office lat/lng becomes known (or changes), recenter the map to include it
  useEffect(() => {
    if (!phlebLatLng || !mapRef.current || !window.google || directions) return;
    const located = stops.filter((s) => s.lat && s.lng);
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(phlebLatLng);
    located.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
    mapRef.current.fitBounds(bounds, 80);
    if (located.length === 0) mapRef.current.setZoom(13);
  }, [phlebLatLng, directions]); // eslint-disable-line

  // Clear the route from the map when all stops get unchecked (so old navigation doesn't linger over the New Patient ETA flow)
  const prevSelectedSize = useRef(0);
  useEffect(() => {
    if (prevSelectedSize.current > 0 && selectedStopIds.size === 0) {
      setDirections(null);
      setRouteInfo(null);
      setShowDirectionsPanel(false);
    }
    prevSelectedSize.current = selectedStopIds.size;
  }, [selectedStopIds]);

  // Fetch today's assigned submissions
  useEffect(() => {
    setHasGeocoded(false); // Reset so new stops get auto-geocoded
    fetchSubmissions();
  }, [phlebotomistId, dateFilter]); // eslint-disable-line

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('submissions')
        .select('id, patient_name, patient_address, patient_street, patient_city, patient_state, patient_zip, patient_lat, patient_lng, status, blood_collection_time, draw_code, lab_order_number, client_name, doctor_name')
        .eq('phlebotomist_id', phlebotomistId)
        .neq('status', 'completed')
        .neq('status', 'cancelled')
        .order('blood_collection_time', { ascending: true });

      const { data, error } = await query;
      if (error) throw error;

      setSubmissions(data || []);

      // Build stops with addresses
      const stopsData = (data || []).filter(s => {
        const addr = s.patient_address || [s.patient_street, s.patient_city, s.patient_state, s.patient_zip].filter(Boolean).join(', ');
        return addr && addr.trim();
      }).map(s => ({
        ...s,
        address: s.patient_address || [s.patient_street, s.patient_city, s.patient_state, s.patient_zip].filter(Boolean).join(', '),
        lat: s.patient_lat ? parseFloat(s.patient_lat) : null,
        lng: s.patient_lng ? parseFloat(s.patient_lng) : null,
      }));

      setStops(stopsData);
      // Start with all stops unchecked — admin/phleb opts in to which stops to plan
      setSelectedStopIds(new Set());
    } catch (err) {
      console.error('Fetch error:', err);
      toast({ title: 'Error loading submissions', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Geocode stops that don't have lat/lng
  const geocodeStops = useCallback(async () => {
    if (!isLoaded || !window.google) return;
    setGeocoding(true);
    const geocoder = new window.google.maps.Geocoder();
    const updated = [...stops];
    let geocoded = 0;

    for (let i = 0; i < updated.length; i++) {
      if (updated[i].lat && updated[i].lng) continue;
      try {
        // Rate limit: 200ms delay between geocode requests
        if (geocoded > 0) await new Promise(r => setTimeout(r, 200));
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: updated[i].address }, (results, status) => {
            if (status === 'OK' && results[0]) resolve(results[0].geometry.location);
            else reject(new Error(status));
          });
        });
        updated[i].lat = result.lat();
        updated[i].lng = result.lng();
        geocoded++;

        // Save to DB
        await supabase.from('submissions').update({
          patient_lat: result.lat(),
          patient_lng: result.lng(),
        }).eq('id', updated[i].id);
      } catch (err) {
        console.error(`Geocode failed for "${updated[i].address}":`, err);
      }
    }

    setStops(updated);
    setGeocoding(false);
    // Only mark as fully geocoded if all stops have coords (otherwise allow retry)
    const allGeocoded = updated.every(s => s.lat && s.lng);
    if (allGeocoded) setHasGeocoded(true);
    if (geocoded > 0) {
      toast({ title: `${geocoded} address(es) geocoded`, status: 'success', duration: 2000 });
    }
  }, [isLoaded, stops, toast]);

  // Auto-geocode once when stops first load (not on every change)
  useEffect(() => {
    if (isLoaded && !hasGeocoded && stops.length > 0 && stops.some(s => !s.lat || !s.lng)) {
      geocodeStops();
    }
  }, [isLoaded, hasGeocoded, stops.length]); // eslint-disable-line

  // Calculate optimized route
  const calculateRoute = useCallback(async () => {
    if (!isLoaded || !window.google) return;
    let validStops = stops.filter(s => s.lat && s.lng && selectedStopIds.has(s.id));
    if (validStops.length < 2) {
      toast({ title: 'Select at least 2 stops to plan a route', description: 'Use the checkboxes to select stops.', status: 'warning', duration: 3000 });
      return;
    }

    // Deduplicate stops at same location — Google Directions can't route between identical points
    const seen = new Set();
    const uniqueStops = validStops.filter(s => {
      const key = `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (uniqueStops.length < 2) {
      toast({ title: 'All stops at same location', description: `${validStops.length} stops are all at the same address — no route to optimize.`, status: 'info', duration: 5000 });
      return;
    }

    validStops = uniqueStops;

    // Google Directions API limit: 25 waypoints max
    if (validStops.length > 27) {
      toast({ title: `Too many stops (${validStops.length})`, description: 'Google Maps supports up to 27 stops. Showing first 27.', status: 'warning', duration: 5000 });
      validStops = validStops.slice(0, 27);
    }

    setRouteLoading(true);
    setDirections(null);

    const directionsService = new window.google.maps.DirectionsService();
    // Use phleb's company address as origin if available
    const origin = phleb?.company_address
      ? phleb.company_address
      : { lat: validStops[0].lat, lng: validStops[0].lng };
    const destination = { lat: validStops[validStops.length - 1].lat, lng: validStops[validStops.length - 1].lng };
    // If using phleb address as origin, all stops except last become waypoints
    const waypointStops = phleb?.company_address ? validStops.slice(0, -1) : validStops.slice(1, -1);
    const waypoints = waypointStops.map(s => ({
      location: { lat: s.lat, lng: s.lng },
      stopover: true,
    }));

    try {
      const result = await new Promise((resolve, reject) => {
        directionsService.route({
          origin,
          destination,
          waypoints,
          optimizeWaypoints: true,
          travelMode: window.google.maps.TravelMode.DRIVING,
        }, (result, status) => {
          if (status === 'OK') resolve(result);
          else reject(new Error(`Directions failed: ${status}`));
        });
      });

      setDirections(result);

      // Calculate total distance and time
      const route = result.routes[0];
      let totalDistance = 0;
      let totalDuration = 0;
      route.legs.forEach(leg => {
        totalDistance += leg.distance.value;
        totalDuration += leg.duration.value;
      });

      setRouteInfo({
        distance: (totalDistance / 1609.34).toFixed(1), // miles
        duration: Math.round(totalDuration / 60), // minutes
        legs: route.legs.map(leg => ({
          distance: leg.distance.text,
          duration: leg.duration.text,
          durationValue: leg.duration.value,
          start: leg.start_address,
          end: leg.end_address,
        })),
        waypointOrder: route.waypoint_order,
      });

      // Reorder stops based on optimized order — preserve stops without coordinates at the end.
      //
      // The reorder must mirror whichever waypoint array was actually SENT to Google
      // (waypointStops above). Otherwise route.waypoint_order references entries that
      // aren't in `middle` and we hit "Cannot read properties of undefined (reading 'id')":
      //   - With company_address origin: waypoints = validStops.slice(0, -1) — all
      //     non-destination stops, including the first. middle must be that full slice.
      //   - Without it: validStops[0] is the origin, waypoints = slice(1, -1).
      if (route.waypoint_order && route.waypoint_order.length > 0) {
        let reorderedValid;
        if (phleb?.company_address) {
          reorderedValid = [
            ...route.waypoint_order.map(i => waypointStops[i]),
            validStops[validStops.length - 1],
          ];
        } else {
          const middle = validStops.slice(1, -1);
          reorderedValid = [
            validStops[0],
            ...route.waypoint_order.map(i => middle[i]),
            validStops[validStops.length - 1],
          ];
        }
        // Defensive: skip any undefined entries (shouldn't happen now but keeps a
        // malformed Google response from crashing the whole UI).
        reorderedValid = reorderedValid.filter(Boolean);
        const validIds = new Set(reorderedValid.map(s => s.id));
        const ungeocoded = stops.filter(s => !validIds.has(s.id));
        setStops([...reorderedValid, ...ungeocoded]);
      }

      toast({ title: 'Route optimized!', description: `${(totalDistance / 1609.34).toFixed(1)} miles, ~${Math.round(totalDuration / 60)} min`, status: 'success', duration: 4000 });
    } catch (err) {
      console.error('Route error:', err);
      toast({ title: 'Route calculation failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setRouteLoading(false);
    }
  }, [isLoaded, stops, toast, phleb, selectedStopIds]);

  // Show route on our embedded map + directions panel (no new tab)
  const startNavigationOnSite = async () => {
    const validStops = stops.filter(s => s.lat && s.lng && selectedStopIds.has(s.id));
    if (validStops.length === 0) {
      toast({ title: 'No stops selected', description: 'Use the checkboxes to select at least one stop.', status: 'warning', duration: 3000 });
      return;
    }

    // Check if all selected stops are at the same location
    const seen = new Set();
    const uniqueLocations = validStops.filter(s => {
      const key = `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Single unique destination — calculate one-way route from phleb origin to that location
    if (uniqueLocations.length === 1) {
      if (isLoaded && window.google && phleb?.company_address) {
        setRouteLoading(true);
        try {
          const directionsService = new window.google.maps.DirectionsService();
          const result = await new Promise((resolve, reject) => {
            directionsService.route({
              origin: phleb.company_address,
              destination: uniqueLocations[0].address || { lat: uniqueLocations[0].lat, lng: uniqueLocations[0].lng },
              travelMode: window.google.maps.TravelMode.DRIVING,
            }, (result, status) => {
              if (status === 'OK') resolve(result);
              else reject(new Error(`Directions failed: ${status}`));
            });
          });
          setDirections(result);
          const leg = result.routes[0].legs[0];
          setRouteInfo({
            distance: (leg.distance.value / 1609.34).toFixed(1),
            duration: Math.round(leg.duration.value / 60),
            legs: [{ distance: leg.distance.text, duration: leg.duration.text, durationValue: leg.duration.value, start: leg.start_address, end: leg.end_address }],
          });
          setShowDirectionsPanel(true);
          if (validStops.length > 1) {
            toast({ title: `${validStops.length} patients at same address`, description: 'Showing single route to that location.', status: 'info', duration: 4000 });
          }
        } catch (err) {
          toast({ title: 'Could not calculate route', description: err.message, status: 'error', duration: 4000 });
        } finally {
          setRouteLoading(false);
        }
      } else {
        toast({ title: 'All stops at same location', description: 'Use "Open in Google Maps" to navigate.', status: 'info', duration: 4000 });
      }
      return;
    }

    // Multi-stop route
    if (!directions) {
      await calculateRoute();
    }
    setShowDirectionsPanel(true);
    if (mapRef.current && validStops.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      validStops.forEach(s => bounds.extend({ lat: s.lat, lng: s.lng }));
      mapRef.current.fitBounds(bounds, 80);
    }
  };

  // Open Google Maps app for actual driving navigation
  const openInGoogleMaps = () => {
    const validStops = stops.filter(s => s.lat && s.lng && s.address && selectedStopIds.has(s.id));
    if (validStops.length === 0) {
      toast({ title: 'No stops selected', description: 'Use the checkboxes to select at least one stop.', status: 'warning', duration: 3000 });
      return;
    }

    // Deduplicate by address (case-insensitive trim)
    const seen = new Set();
    const uniqueStops = validStops.filter(s => {
      const key = s.address.toLowerCase().trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Use phleb's company address as origin
    const phlebOrigin = phleb?.company_address || null;

    // Single unique location — open directions from phleb address to the stop's address
    if (uniqueStops.length === 1) {
      const dest = encodeURIComponent(uniqueStops[0].address);
      const originParam = phlebOrigin ? `&origin=${encodeURIComponent(phlebOrigin)}` : '';
      const url = `https://www.google.com/maps/dir/?api=1${originParam}&destination=${dest}&travelmode=driving`;
      window.open(url, '_blank');
      if (validStops.length > uniqueStops.length) {
        toast({ title: 'All stops at same location', description: `${validStops.length} patients at ${uniqueStops[0].address}`, status: 'info', duration: 4000 });
      }
      return;
    }

    // Multi-stop: use addresses for clearer Google Maps pins
    const originAddr = phlebOrigin ? phlebOrigin : uniqueStops[0].address;
    const destinationAddr = uniqueStops[uniqueStops.length - 1].address;
    const waypointStops = phlebOrigin ? uniqueStops.slice(0, -1) : uniqueStops.slice(1, -1);
    const waypoints = waypointStops.map(s => s.address).join('|');

    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(originAddr)}&destination=${encodeURIComponent(destinationAddr)}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ''}&travelmode=driving`;
    window.open(url, '_blank');
  };

  // Click stop in list — geocode if needed, pan map, select
  const handleStopClick = async (stop) => {
    setSelectedStop(stop);

    // Pan map if stop has coordinates
    if (stop.lat && stop.lng && mapRef.current) {
      mapRef.current.panTo({ lat: stop.lat, lng: stop.lng });
      mapRef.current.setZoom(16); // Zoom in close enough to see street details
      return;
    }

    // Geocode on-demand if not yet geocoded
    if (!isLoaded || !window.google || !stop.address) return;
    try {
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: stop.address }, (results, status) => {
          if (status === 'OK' && results[0]) resolve(results[0].geometry.location);
          else reject(new Error(status));
        });
      });
      const lat = result.lat();
      const lng = result.lng();
      // Update local state
      setStops(prev => prev.map(s => s.id === stop.id ? { ...s, lat, lng } : s));
      // Only update selectedStop and pan if user is still viewing this stop
      setSelectedStop(curr => curr?.id === stop.id ? { ...stop, lat, lng } : curr);
      // Save to DB
      await supabase.from('submissions').update({ patient_lat: lat, patient_lng: lng }).eq('id', stop.id);
      // Pan map only if still selected
      setSelectedStop(curr => {
        if (curr?.id === stop.id && mapRef.current) {
          mapRef.current.panTo({ lat, lng });
          mapRef.current.setZoom(14);
        }
        return curr;
      });
    } catch (err) {
      toast({ title: 'Could not geocode address', description: stop.address, status: 'warning', duration: 3000 });
    }
  };

  // Move stop up/down
  const moveStop = (index, direction) => {
    const newStops = [...stops];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newStops.length) return;
    [newStops[index], newStops[swapIndex]] = [newStops[swapIndex], newStops[index]];
    setStops(newStops);
    setDirections(null); // Clear route when reordered manually
  };

  // Always prefer the phleb's office as the initial map center so the user sees their starting point
  const firstGeocodedStop = stops.find(s => s.lat && s.lng);
  const mapCenter = phlebLatLng
    || (firstGeocodedStop ? { lat: firstGeocodedStop.lat, lng: firstGeocodedStop.lng } : defaultCenter);

  if (!GOOGLE_MAPS_KEY) {
    return (
      <Box p={8} textAlign="center">
        <Text color="red.500" fontWeight="bold">Google Maps API key not configured</Text>
        <Text color="gray.500" mt={2}>Add REACT_APP_GOOGLE_MAPS_KEY to environment variables</Text>
      </Box>
    );
  }

  const validStopsCount = stops.filter(s => s.lat && s.lng).length;
  const selectedValidStops = stops.filter(s => s.lat && s.lng && selectedStopIds.has(s.id));
  const selectedValidCount = selectedValidStops.length;

  return (
    <Box maxW="1400px" mx="auto">
      {/* Hero Header — gradient with glassy effect (hidden in admin mode) */}
      {!hideHeader && (
        <Box
          bgGradient="linear(135deg, teal.500, blue.600)"
          borderRadius="2xl"
          p={6}
          mb={5}
          color="white"
          boxShadow="xl"
          position="relative"
          overflow="hidden"
        >
          {/* Decorative circles */}
          <Box position="absolute" top={-10} right={-10} w="200px" h="200px" bg="whiteAlpha.100" borderRadius="full" />
          <Box position="absolute" bottom={-20} left="40%" w="240px" h="240px" bg="whiteAlpha.50" borderRadius="full" />

          <Flex justify="space-between" align="center" position="relative" flexWrap="wrap" gap={4}>
            <HStack spacing={4}>
              <Box bg="white" p={2} borderRadius="xl" cursor="pointer" onClick={() => navigate(`/phlebotomist/${phlebotomistId}`)}>
                <Image src="/logo.png" alt="AtHomeBloodDraw.com" h="36px" objectFit="contain" />
              </Box>
              <Box>
                <Heading size="lg" fontWeight="800" letterSpacing="-0.5px">Route Planner</Heading>
                <Text fontSize="sm" opacity={0.9}>{phleb?.company_name || phleb?.full_name || 'Phlebotomist'}</Text>
              </Box>
            </HStack>
            <Button
              size="sm"
              bg="whiteAlpha.300"
              color="white"
              _hover={{ bg: 'whiteAlpha.400' }}
              backdropFilter="blur(10px)"
              onClick={() => navigate(`/phlebotomist/${phlebotomistId}`)}
              borderRadius="full"
              px={5}
            >
              ← Back to Dashboard
            </Button>
          </Flex>
        </Box>
      )}

      {/* Compact action bar — date/time inputs, inline stat chips, primary buttons */}
      <Card bg="white" boxShadow="sm" borderRadius="xl" mb={4} overflow="hidden">
        <CardBody py={3} px={4}>
          <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
            <HStack spacing={2} flexWrap="wrap">
              <Tooltip label="Route date" placement="top">
                <Input type="date" size="sm" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} w="150px" borderRadius="md" />
              </Tooltip>
              <Tooltip label="Departure time" placement="top">
                <Input type="time" size="sm" value={departureTime} onChange={(e) => setDepartureTime(e.target.value)} w="110px" borderRadius="md" />
              </Tooltip>
              {/* Stat chips — inline, compact */}
              <HStack spacing={1.5} ml={1} flexWrap="wrap" rowGap={1}>
                <Badge colorScheme="gray" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="700">
                  {stops.length} stops
                </Badge>
                {validStopsCount < stops.length && (
                  <Badge colorScheme="green" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="700">
                    {validStopsCount} located
                  </Badge>
                )}
                {routeInfo && (
                  <>
                    <Badge colorScheme="blue" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="700">
                      {routeInfo.distance} mi
                    </Badge>
                    <Badge colorScheme="orange" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="700">
                      {routeInfo.duration} min
                    </Badge>
                    {(() => {
                      const finalEta = getEtaAtLeg(routeInfo.legs.length - 1);
                      return finalEta ? (
                        <Badge colorScheme="purple" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="700">
                          Arrive {formatClock(finalEta)}
                        </Badge>
                      ) : null;
                    })()}
                  </>
                )}
              </HStack>
            </HStack>
            <HStack spacing={2}>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FaRoute />}
                onClick={calculateRoute}
                isLoading={routeLoading}
                loadingText="Optimizing"
                isDisabled={selectedValidCount < 2}
                borderRadius="md"
                fontWeight="700"
              >
                Optimize
              </Button>
              <Button
                size="sm"
                bgGradient="linear(to-r, green.400, teal.500)"
                color="white"
                _hover={{ bgGradient: 'linear(to-r, green.500, teal.600)' }}
                leftIcon={<FaPlay />}
                onClick={startNavigationOnSite}
                isDisabled={selectedValidCount === 0}
                borderRadius="md"
                fontWeight="700"
              >
                Navigate
              </Button>
              {directions && (
                <Tooltip label="Open in Google Maps app" placement="top">
                  <IconButton
                    size="sm"
                    icon={<FaExternalLinkAlt />}
                    onClick={openInGoogleMaps}
                    variant="outline"
                    borderRadius="md"
                    aria-label="Open in Google Maps"
                  />
                </Tooltip>
              )}
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* New Patient ETA — collapsed by default; opens on demand for phone-call scheduling */}
      <Card bg="white" boxShadow="sm" borderRadius="xl" mb={4} overflow="hidden">
        <Flex
          bgGradient={showNewPatientPanel ? "linear(to-r, purple.500, pink.500)" : "linear(to-r, gray.50, white)"}
          px={4}
          py={2.5}
          align="center"
          justify="space-between"
          cursor="pointer"
          onClick={() => setShowNewPatientPanel((v) => !v)}
          _hover={{ filter: 'brightness(0.98)' }}
        >
          <HStack spacing={3} color={showNewPatientPanel ? 'white' : 'gray.700'}>
            <Icon as={FaUserPlus} boxSize={3.5} />
            <Text fontWeight="700" fontSize="sm">Quick ETA for a new patient</Text>
            {!showNewPatientPanel && (
              <Text fontSize="xs" color="gray.500">— calculate arrival from an address</Text>
            )}
          </HStack>
          <Icon as={showNewPatientPanel ? FaChevronUp : FaChevronDown} boxSize={3} color={showNewPatientPanel ? 'white' : 'gray.500'} />
        </Flex>
        {showNewPatientPanel && (
        <CardBody py={4} px={5}>
          <Flex gap={3} align="end" flexWrap="wrap">
            <Box flex={1} minW="280px">
              <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1} textTransform="uppercase" letterSpacing="0.5px">Patient Address</Text>
              <Input
                placeholder="123 Main St, Brooklyn, NY 11201"
                value={newPatientAddress}
                onChange={(e) => setNewPatientAddress(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') estimateNewPatientEta(); }}
                size="sm"
                borderRadius="md"
              />
            </Box>
            <Button
              leftIcon={<FaCalculator />}
              bgGradient="linear(to-r, purple.500, pink.500)"
              color="white"
              _hover={{ bgGradient: 'linear(to-r, purple.600, pink.600)' }}
              _active={{ bgGradient: 'linear(to-r, purple.700, pink.700)' }}
              onClick={estimateNewPatientEta}
              isLoading={newPatientLoading}
              loadingText="Calculating..."
              size="sm"
              borderRadius="md"
              fontWeight="700"
              px={5}
            >
              Estimate Arrival
            </Button>
            {newPatientEta && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  // Only clear the map if the route currently shown came from this New Patient ETA;
                  // otherwise leave the user's optimized route alone. Restore any stashed route if we had overwritten one.
                  const isNewPatientRouteOnMap = directions && directions === newPatientEta.directDirections;
                  if (isNewPatientRouteOnMap) {
                    if (newPatientEta.stashedRoute) {
                      setDirections(newPatientEta.stashedRoute.directions || null);
                      setRouteInfo(newPatientEta.stashedRoute.routeInfo || null);
                    } else {
                      setDirections(null);
                      setRouteInfo(null);
                    }
                  }
                  setNewPatientEta(null);
                  setNewPatientAddress('');
                }}
                borderRadius="md"
              >
                Clear
              </Button>
            )}
          </Flex>

          {newPatientEta && (
            <Box mt={4} p={4} bg="gray.50" borderRadius="lg" border="1px" borderColor="gray.200">
              <Text fontSize="xs" color="gray.500" fontWeight="600" mb={2} noOfLines={1}>
                <Icon as={FaMapMarkerAlt} boxSize={3} mr={1} color="purple.500" />
                {newPatientEta.formattedAddress}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {newPatientEta.direct && (
                  <Box bg="white" p={3} borderRadius="md" border="1px" borderColor="purple.100">
                    <HStack mb={2}>
                      <Box bg="purple.500" color="white" w={6} h={6} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="2xs" fontWeight="800">A</Box>
                      <Text fontSize="xs" fontWeight="700" color="gray.700" textTransform="uppercase">Go directly now</Text>
                    </HStack>
                    <HStack spacing={4}>
                      <Box>
                        <Text fontSize="2xs" color="gray.500" fontWeight="600">Arrives</Text>
                        <Text fontSize="xl" fontWeight="800" color="purple.600">{formatClock(newPatientEta.direct.eta)}</Text>
                      </Box>
                      <Divider orientation="vertical" h="40px" />
                      <Box>
                        <Text fontSize="2xs" color="gray.500" fontWeight="600">Drive</Text>
                        <Text fontSize="sm" fontWeight="700" color="gray.700">{newPatientEta.direct.durationText}</Text>
                        <Text fontSize="2xs" color="gray.500">{newPatientEta.direct.distanceText}</Text>
                      </Box>
                    </HStack>
                    <Text fontSize="2xs" color="gray.400" mt={1.5}>From {phleb?.company_address ? 'your office' : 'first stop'}, departing {departureTime}</Text>
                  </Box>
                )}
                {newPatientEta.afterRoute && (
                  <Box bg="white" p={3} borderRadius="md" border="1px" borderColor="pink.100">
                    <HStack mb={2}>
                      <Box bg="pink.500" color="white" w={6} h={6} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="2xs" fontWeight="800">B</Box>
                      <Text fontSize="xs" fontWeight="700" color="gray.700" textTransform="uppercase">After today's route</Text>
                    </HStack>
                    {newPatientEta.afterRoute.eta ? (
                      <HStack spacing={4}>
                        <Box>
                          <Text fontSize="2xs" color="gray.500" fontWeight="600">Arrives</Text>
                          <Text fontSize="xl" fontWeight="800" color="pink.600">{formatClock(newPatientEta.afterRoute.eta)}</Text>
                        </Box>
                        <Divider orientation="vertical" h="40px" />
                        <Box>
                          <Text fontSize="2xs" color="gray.500" fontWeight="600">Added drive</Text>
                          <Text fontSize="sm" fontWeight="700" color="gray.700">+{newPatientEta.afterRoute.addedDurationText}</Text>
                          <Text fontSize="2xs" color="gray.500">+{newPatientEta.afterRoute.addedDistanceText}</Text>
                        </Box>
                      </HStack>
                    ) : (
                      <Text fontSize="xs" color="gray.500">{newPatientEta.afterRoute.note}</Text>
                    )}
                    <Text fontSize="2xs" color="gray.400" mt={1.5}>If added as the last stop after {selectedValidStops.length} current stop{selectedValidStops.length === 1 ? '' : 's'}</Text>
                  </Box>
                )}
                {!newPatientEta.direct && !newPatientEta.afterRoute && (
                  <Text fontSize="xs" color="gray.500" gridColumn="span 2">
                    Could not calculate route. Make sure your office address is set in your profile.
                  </Text>
                )}
              </SimpleGrid>
            </Box>
          )}
        </CardBody>
        )}
      </Card>

      <SimpleGrid columns={{ base: 1, lg: 5 }} spacing={5}>
        {/* Map - takes 3/5 of the width on large screens */}
        <Box position="relative" gridColumn={{ lg: 'span 3' }}>
          {!isLoaded ? (
            <Flex h="600px" bg="gray.50" borderRadius="2xl" justify="center" align="center" border="1px" borderColor="gray.200">
              <VStack spacing={3}>
                <Spinner size="xl" color="teal.500" thickness="3px" />
                <Text fontSize="sm" color="gray.500">Loading map...</Text>
              </VStack>
            </Flex>
          ) : (
            <Box borderRadius="2xl" overflow="hidden" boxShadow="xl" border="1px" borderColor="gray.200">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={13}
                onLoad={(map) => {
                  mapRef.current = map;
                  // Auto-fit bounds to include the phleb's office + all geocoded stops
                  setTimeout(() => {
                    if (!window.google) return;
                    const located = stops.filter(s => s.lat && s.lng);
                    const bounds = new window.google.maps.LatLngBounds();
                    if (phlebLatLng) bounds.extend(phlebLatLng);
                    located.forEach(s => bounds.extend({ lat: s.lat, lng: s.lng }));
                    if (!bounds.isEmpty()) {
                      map.fitBounds(bounds, 80);
                      // If only one point in bounds, fitBounds zooms too close
                      if (located.length === 0 && phlebLatLng) {
                        map.setZoom(13);
                      } else if (located.length === 1 && !phlebLatLng) {
                        map.setZoom(15);
                      }
                    }
                  }, 500);
                }}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true, styles: mapStyles }}
              >
                {directions && <DirectionsRenderer directions={directions} options={{ suppressMarkers: false }} />}
                {/* Phleb office marker — small teal home pin shown when there's no route on the map */}
                {!directions && phlebLatLng && window.google && (
                  <Marker
                    position={phlebLatLng}
                    icon={{
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44"><path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 26 18 26s18-12.5 18-26C36 8.06 27.94 0 18 0z" fill="#14b8a6" stroke="white" stroke-width="2.5"/><path d="M18 9l-7 6v9h4v-6h6v6h4v-9z" fill="white"/></svg>`)}`,
                      scaledSize: new window.google.maps.Size(32, 39),
                      anchor: new window.google.maps.Point(16, 39),
                    }}
                    title={phleb?.company_name || phleb?.full_name || 'Office'}
                    zIndex={1}
                  />
                )}
                {!directions && selectedStop && selectedStop.lat && selectedStop.lng && [selectedStop].map((stop) => {
                  const listIndex = stops.findIndex(s => s.id === stop.id);
                  const isSelected = true;
                  // Color based on status
                  const sColor = stop.status === 'completed' ? '#22c55e'
                    : stop.status === 'in_progress' ? '#3b82f6'
                    : (stop.status || '').includes('waiting') ? '#f97316'
                    : '#9333ea';
                  const fillColor = isSelected ? '#0ea5e9' : sColor;
                  // Inline SVG pin as data URL — large and very visible
                  const svgPin = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="60" viewBox="0 0 48 60">
                    <defs>
                      <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
                        <feOffset dx="0" dy="2" result="offsetblur"/>
                        <feComponentTransfer><feFuncA type="linear" slope="0.4"/></feComponentTransfer>
                        <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
                      </filter>
                    </defs>
                    <path filter="url(#shadow)" d="M24 0C10.7 0 0 10.7 0 24c0 18 24 36 24 36s24-18 24-36C48 10.7 37.3 0 24 0z" fill="${fillColor}" stroke="white" stroke-width="3"/>
                    <circle cx="24" cy="24" r="11" fill="white"/>
                    <text x="24" y="28" font-family="Arial, sans-serif" font-size="14" font-weight="bold" fill="${fillColor}" text-anchor="middle">${listIndex + 1}</text>
                  </svg>`;
                  const pinIcon = window.google ? {
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgPin)}`,
                    scaledSize: new window.google.maps.Size(isSelected ? 56 : 44, isSelected ? 70 : 55),
                    anchor: new window.google.maps.Point(isSelected ? 28 : 22, isSelected ? 70 : 55),
                  } : null;
                  return (
                    <Marker
                      key={`${stop.id}-${isSelected ? 'selected' : 'default'}`}
                      position={{ lat: stop.lat, lng: stop.lng }}
                      icon={pinIcon}
                      onClick={() => handleStopClick(stop)}
                      animation={isSelected && window.google ? window.google.maps.Animation.DROP : null}
                      zIndex={isSelected ? 999 : 1}
                    />
                  );
                })}
              </GoogleMap>
            </Box>
          )}

          {/* Geocoding indicator */}
          {geocoding && (
            <Box position="absolute" bottom={4} left={4} bg="white" px={3} py={2} borderRadius="full" boxShadow="lg" border="1px" borderColor="blue.200">
              <HStack spacing={2}>
                <Spinner size="xs" color="blue.500" />
                <Text fontSize="xs" color="blue.600" fontWeight="600">Locating addresses...</Text>
              </HStack>
            </Box>
          )}

          {/* Selected Stop Detail Card — glassy floating panel */}
          {selectedStop && selectedStop.lat && (
            <Box
              position="absolute"
              top={4}
              left={4}
              right={4}
              maxW="380px"
              bg="white"
              borderRadius="xl"
              boxShadow="2xl"
              overflow="hidden"
              zIndex={5}
              border="1px"
              borderColor="gray.100"
            >
              <Box bgGradient="linear(to-r, blue.500, teal.500)" px={4} py={2}>
                <Flex justify="space-between" align="center">
                  <HStack spacing={2}>
                    <Box bg="white" color="blue.600" w={6} h={6} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="xs" fontWeight="800">
                      {stops.findIndex(s => s.id === selectedStop.id) + 1}
                    </Box>
                    <Text color="white" fontSize="xs" fontWeight="700">SELECTED STOP</Text>
                  </HStack>
                  <IconButton icon={<FaTimes />} size="xs" variant="ghost" color="white" _hover={{ bg: 'whiteAlpha.300' }} onClick={() => setSelectedStop(null)} aria-label="Close" />
                </Flex>
              </Box>
              <Box p={4}>
                <Text fontWeight="700" fontSize="md" color="gray.800" noOfLines={1}>{selectedStop.patient_name}</Text>
                <HStack spacing={1.5} mt={0.5} color="gray.600">
                  <Icon as={FaMapMarkerAlt} boxSize={3} />
                  <Text fontSize="xs" noOfLines={1}>{selectedStop.address}</Text>
                </HStack>
                {selectedStop.client_name && (
                  <Text fontSize="xs" color="gray.500" mt={1} noOfLines={1}>📍 {selectedStop.client_name}</Text>
                )}
                <HStack spacing={2} mt={3}>
                  <Badge colorScheme={statusColor(selectedStop.status)} fontSize="2xs" px={2} py={0.5} borderRadius="full">
                    {(selectedStop.status || 'pending').replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                  {selectedStop.draw_code && (
                    <Badge variant="outline" fontSize="2xs" fontFamily="mono" px={2} py={0.5} borderRadius="full">
                      #{selectedStop.draw_code}
                    </Badge>
                  )}
                  {selectedStop.lab_order_number && (
                    <Badge colorScheme="blue" fontSize="2xs" fontFamily="mono" px={2} py={0.5} borderRadius="full">
                      Order #{selectedStop.lab_order_number}
                    </Badge>
                  )}
                </HStack>
              </Box>
            </Box>
          )}
        </Box>

        {/* Stop List - 2/5 width */}
        <Box gridColumn={{ lg: 'span 2' }}>
          <Card bg="white" borderRadius="2xl" boxShadow="md" overflow="hidden" border="1px" borderColor="gray.100">
            <Box bgGradient="linear(to-r, gray.50, white)" px={5} py={4} borderBottom="1px" borderColor="gray.100">
              <Flex justify="space-between" align="center" mb={stops.length > 0 ? 3 : 0}>
                <HStack spacing={2}>
                  <Box bg="teal.500" color="white" p={1.5} borderRadius="md">
                    <Icon as={FaMapMarkerAlt} boxSize={3.5} />
                  </Box>
                  <Box>
                    <Text fontWeight="800" fontSize="sm" color="gray.800">Today's Stops</Text>
                    <Text fontSize="xs" color="gray.500">
                      {selectedStopIds.size} of {stops.length} selected
                    </Text>
                  </Box>
                </HStack>
                <IconButton
                  size="sm"
                  variant="ghost"
                  icon={<FaRedo />}
                  onClick={fetchSubmissions}
                  aria-label="Refresh"
                  borderRadius="full"
                />
              </Flex>
              {/* Select All / Deselect All toggle */}
              {stops.length > 0 && (
                <HStack spacing={2}>
                  <Checkbox
                    isChecked={selectedStopIds.size === stops.length && stops.length > 0}
                    isIndeterminate={selectedStopIds.size > 0 && selectedStopIds.size < stops.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStopIds(new Set(stops.map(s => s.id)));
                      } else {
                        setSelectedStopIds(new Set());
                      }
                    }}
                    colorScheme="teal"
                    size="sm"
                  >
                    <Text fontSize="xs" fontWeight="600" color="gray.600">
                      {selectedStopIds.size === stops.length ? 'Deselect all' : 'Select all'}
                    </Text>
                  </Checkbox>
                </HStack>
              )}
            </Box>

            {loading ? (
              <Flex py={16} justify="center">
                <Spinner color="teal.500" thickness="3px" />
              </Flex>
            ) : stops.length === 0 ? (
              <VStack py={16} spacing={3} px={6}>
                <Box bg="gray.50" p={5} borderRadius="full">
                  <Icon as={FaMapMarkerAlt} boxSize={8} color="gray.300" />
                </Box>
                <Text color="gray.700" fontSize="sm" fontWeight="600" textAlign="center">No stops scheduled</Text>
                <Text color="gray.400" fontSize="xs" textAlign="center" maxW="220px">No assigned draws with patient addresses for this date.</Text>
              </VStack>
            ) : (
              <Box maxH="540px" overflowY="auto">
                {stops.map((stop, i) => {
                  const isSelected = selectedStop?.id === stop.id;
                  const isLocated = stop.lat && stop.lng;
                  const isChecked = selectedStopIds.has(stop.id);
                  const sColor = statusColor(stop.status);
                  return (
                    <Box
                      key={stop.id}
                      px={4}
                      py={3.5}
                      borderBottom="1px"
                      borderColor="gray.50"
                      cursor="pointer"
                      onClick={() => handleStopClick(stop)}
                      bg={isSelected ? 'blue.50' : (!isChecked ? 'gray.50' : 'transparent')}
                      _hover={{ bg: isSelected ? 'blue.50' : 'gray.100' }}
                      transition="all 0.15s"
                      borderLeft="4px solid"
                      borderLeftColor={isSelected ? 'blue.500' : 'transparent'}
                    >
                      <Flex gap={3} align="start">
                        {/* Selection checkbox — always full opacity for clear interaction */}
                        <Box mt={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            isChecked={isChecked}
                            onChange={(e) => {
                              const next = new Set(selectedStopIds);
                              if (e.target.checked) next.add(stop.id);
                              else next.delete(stop.id);
                              setSelectedStopIds(next);
                            }}
                            colorScheme="teal"
                            size="lg"
                          />
                        </Box>
                        {/* Faded content for unchecked stops, but checkbox stays visible */}
                        <Box flex={1} opacity={isChecked ? 1 : 0.5}>
                        <Flex gap={3} align="start">
                        {/* Number badge with status color */}
                        <Flex direction="column" align="center" gap={1} flexShrink={0}>
                          <Box
                            w={8}
                            h={8}
                            borderRadius="full"
                            bgGradient={isLocated ? `linear(135deg, ${sColor}.400, ${sColor}.600)` : 'linear(135deg, gray.300, gray.400)'}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            color="white"
                            fontWeight="800"
                            fontSize="sm"
                            boxShadow="md"
                          >
                            {i + 1}
                          </Box>
                          <VStack spacing={0}>
                            <IconButton
                              icon={<FaArrowUp />}
                              size="xs"
                              variant="ghost"
                              minW={5}
                              h={5}
                              isDisabled={i === 0}
                              onClick={(e) => { e.stopPropagation(); moveStop(i, -1); }}
                              aria-label="Move up"
                            />
                            <IconButton
                              icon={<FaArrowDown />}
                              size="xs"
                              variant="ghost"
                              minW={5}
                              h={5}
                              isDisabled={i === stops.length - 1}
                              onClick={(e) => { e.stopPropagation(); moveStop(i, 1); }}
                              aria-label="Move down"
                            />
                          </VStack>
                        </Flex>

                        {/* Stop info */}
                        <Box flex={1} minW={0}>
                          <HStack spacing={2} mb={0.5}>
                            <Text fontWeight="700" fontSize="sm" color="gray.800" noOfLines={1}>{stop.patient_name}</Text>
                            {!isLocated && (
                              <Badge colorScheme="yellow" fontSize="2xs" borderRadius="full" px={2}>📍 Locating...</Badge>
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.500" noOfLines={1} mb={1.5}>{stop.address}</Text>
                          <HStack spacing={1.5} flexWrap="wrap">
                            <Badge
                              colorScheme={sColor}
                              fontSize="2xs"
                              borderRadius="full"
                              px={2}
                              py={0.5}
                              textTransform="uppercase"
                              fontWeight="700"
                            >
                              {(stop.status || 'pending').replace(/_/g, ' ')}
                            </Badge>
                            {stop.draw_code && (
                              <Badge variant="outline" fontSize="2xs" fontFamily="mono" borderRadius="full" px={2}>
                                #{stop.draw_code}
                              </Badge>
                            )}
                            {stop.lab_order_number && (
                              <Badge colorScheme="blue" fontSize="2xs" fontFamily="mono" borderRadius="full" px={2}>
                                Order #{stop.lab_order_number}
                              </Badge>
                            )}
                          </HStack>
                          {stop.client_name && (
                            <Text fontSize="2xs" color="gray.400" mt={1.5} noOfLines={1}>📍 {stop.client_name}</Text>
                          )}
                          {routeInfo?.legs?.[i] && (
                            <VStack spacing={1} align="start" mt={2}>
                              <HStack spacing={2} fontSize="2xs" color="blue.500" fontWeight="600">
                                <HStack spacing={0.5}>
                                  <Icon as={FaCar} boxSize={2.5} />
                                  <Text>{routeInfo.legs[i].distance}</Text>
                                </HStack>
                                <Text>•</Text>
                                <HStack spacing={0.5}>
                                  <Icon as={FaClock} boxSize={2.5} />
                                  <Text>{routeInfo.legs[i].duration}</Text>
                                </HStack>
                              </HStack>
                              {(() => {
                                const eta = getEtaAtLeg(i);
                                return eta ? (
                                  <Badge bgGradient="linear(to-r, purple.500, pink.500)" color="white" fontSize="2xs" px={2} py={0.5} borderRadius="full" fontWeight="700">
                                    Arrives ~{formatClock(eta)}
                                  </Badge>
                                ) : null;
                              })()}
                            </VStack>
                          )}
                        </Box>

                        {/* Quick nav button */}
                        {isLocated && (
                          <Tooltip label="Navigate to this stop" placement="left">
                            <IconButton
                              icon={<FaExternalLinkAlt />}
                              size="sm"
                              variant="ghost"
                              colorScheme="green"
                              onClick={(e) => {
                                e.stopPropagation();
                                const origin = phleb?.company_address ? `&origin=${encodeURIComponent(phleb.company_address)}` : '';
                                const dest = stop.address ? encodeURIComponent(stop.address) : `${stop.lat},${stop.lng}`;
                                window.open(`https://www.google.com/maps/dir/?api=1${origin}&destination=${dest}&travelmode=driving`, '_blank');
                              }}
                              aria-label="Navigate"
                              borderRadius="full"
                            />
                          </Tooltip>
                        )}
                        </Flex>
                        </Box>
                      </Flex>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Card>
        </Box>
      </SimpleGrid>

      {/* Turn-by-turn directions panel — shown after Start Navigation */}
      {showDirectionsPanel && directions && (
        <Card mt={5} bg="white" borderRadius="2xl" boxShadow="md" overflow="hidden">
          <Box bgGradient="linear(to-r, green.500, teal.600)" px={5} py={4}>
            <Flex justify="space-between" align="center">
              <HStack spacing={3} color="white">
                <Icon as={FaPlay} boxSize={4} />
                <Box>
                  <Text fontWeight="800" fontSize="md">Turn-by-Turn Directions</Text>
                  <Text fontSize="xs" opacity={0.9}>
                    {routeInfo?.distance} mi • {routeInfo?.duration} min total
                    {(() => {
                      const finalEta = routeInfo?.legs ? getEtaAtLeg(routeInfo.legs.length - 1) : null;
                      return finalEta ? ` • Arrives ${formatClock(finalEta)}` : '';
                    })()}
                  </Text>
                </Box>
              </HStack>
              <HStack spacing={2}>
                <Button size="sm" bg="whiteAlpha.300" color="white" _hover={{ bg: 'whiteAlpha.400' }} leftIcon={<FaExternalLinkAlt />} onClick={openInGoogleMaps} borderRadius="lg" backdropFilter="blur(10px)">
                  Open in Google Maps
                </Button>
                <IconButton size="sm" icon={<FaTimes />} bg="whiteAlpha.300" color="white" _hover={{ bg: 'whiteAlpha.400' }} onClick={() => setShowDirectionsPanel(false)} aria-label="Close directions" borderRadius="lg" />
              </HStack>
            </Flex>
          </Box>
          <Box maxH="500px" overflowY="auto" px={5} py={4}>
            {directions.routes[0]?.legs?.map((leg, legIdx) => (
              <Box key={legIdx} mb={5}>
                <HStack mb={3} pb={2} borderBottom="2px" borderColor="gray.100">
                  <Box bg="teal.500" color="white" w={7} h={7} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="800" fontSize="xs">
                    {legIdx + 1}
                  </Box>
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="700" noOfLines={1}>{leg.end_address}</Text>
                    <HStack spacing={3} fontSize="xs" color="gray.500" flexWrap="wrap">
                      <HStack spacing={1}><Icon as={FaCar} boxSize={3} /><Text>{leg.distance.text}</Text></HStack>
                      <HStack spacing={1}><Icon as={FaClock} boxSize={3} /><Text>{leg.duration.text}</Text></HStack>
                      {(() => {
                        const eta = getEtaAtLeg(legIdx);
                        return eta ? (
                          <Text fontWeight="700" color="purple.500">Arrive ~{formatClock(eta)}</Text>
                        ) : null;
                      })()}
                    </HStack>
                  </Box>
                </HStack>
                <VStack spacing={2} align="stretch" pl={2}>
                  {leg.steps?.slice(0, 8).map((step, stepIdx) => (
                    <HStack key={stepIdx} spacing={3} p={2} bg="gray.50" borderRadius="md" align="start">
                      <Text fontSize="2xs" color="gray.400" fontWeight="700" minW="24px" mt={0.5}>{stepIdx + 1}.</Text>
                      <Box flex={1}>
                        <Text fontSize="xs" color="gray.700" dangerouslySetInnerHTML={{ __html: step.instructions }} />
                        <Text fontSize="2xs" color="gray.500" mt={0.5}>{step.distance.text} • {step.duration.text}</Text>
                      </Box>
                    </HStack>
                  ))}
                  {leg.steps?.length > 8 && (
                    <Text fontSize="xs" color="gray.400" pl={9}>...and {leg.steps.length - 8} more turns</Text>
                  )}
                </VStack>
              </Box>
            ))}
          </Box>
        </Card>
      )}
    </Box>
  );
}
